import { useCallback, useMemo } from "react";

import {
    isPastNow,
    bsMath,
    getUnixTs,
    TIME,
    convertAnyDate,
    formatDate,
    SortDirection,
    filterTruthy,
    isBeforeNow,
    combineCollections,
    NullableRecord,
    filterNullableRecord
} from "@bridgesplit/utils";
import { useMemoizedKeyMap } from "@bridgesplit/ui";
import { LoanValueTuple, FALLBACK_METADATA, UncertaintyType, WhirlpoolOffchain } from "@bridgesplit/abf-sdk";
import { skipToken } from "@reduxjs/toolkit/query";

import { useLoanInfosQuery, useRaydiumOffchainQuery, useWhirlpoolsOffchainQuery } from "../reducers";
import {
    BsMetaUtil,
    calculateHealthRatio,
    getLoanTotalDebtForLedger,
    getRestrictedMarketsStatus,
    isLiquidateEvent,
    isRepayEvent,
    summarizeLedgerAccount,
    useAbfTypesToUiConverter,
    isCollateralSplMint,
    useGenerateUiExpanded
} from "../utils";
import {
    AbfLoanExpanded,
    LoanStatus,
    LoanFilterApi,
    LoanPaginationInfo,
    LoanInfo,
    LedgerExpanded,
    LoanInfoResponse,
    AssetTypeIdentifier,
    Ledger,
    LoanInfoSorting,
    LoanEventActionType,
    ParsedRepayPrincipalMetadata,
    ParsedLoanEventExpanded,
    LiquidationType,
    PriceFetchType,
    LoanFilterType,
    LoanStatusType,
    Collateral,
    CollateralLoanValues
} from "../types";
import { useOraclePrices } from "./pricing";
import { isWhirlpoolMetadata, useWhirlpoolPositions } from "./whirlpool";
import { ASSET_TIME_RESTRICTIONS, LOAN_REFINANCE_GRACE_BEFORE_DEFAULT } from "../constants";
import {
    getClmmPositionIdentifier,
    getClmmPositionMint,
    getClmmPositionType,
    isRaydiumPosition,
    isWhirlpoolPosition
} from "./clmm";
import { AbfLoanResponse, LoanCollateralExpanded, LoanExpandedBase, LoanInfoExpanded } from "../types/loan";
import { useRaydiumPositions } from "./raydium";

export type LoanInfosParams = {
    loanFilter: LoanFilterApi;
    // require explicit null to avoid ts error
    pagination?: LoanPaginationInfo | null;
    skip?: boolean;
};

export type LoanInfoResult = {
    data: AbfLoanExpanded[] | undefined;
    totalLoans: number | undefined;
    isLoading: boolean;
    isFetching: boolean;
};

export function convertLoanStatusTypeToFilterType(loanStatusType: LoanStatusType): LoanFilterType | undefined {
    switch (loanStatusType) {
        case LoanStatusType.ActiveOnly:
            return LoanFilterType.Active;
        case LoanStatusType.InactiveOnly:
            return LoanFilterType.Closed;
        case LoanStatusType.All:
            return undefined;
    }
}

export function useLoanInfos({ loanFilter: loanFilterRaw, pagination, ...params }: LoanInfosParams): LoanInfoResult {
    const loanFilter = useMemo((): LoanFilterApi => {
        return {
            lenders: loanFilterRaw.lenders,
            loanAddresses: loanFilterRaw.loanAddresses ?? [],
            borrowers: loanFilterRaw.borrowers ?? [],
            filterType: convertLoanStatusTypeToFilterType(pagination?.loanStatusType ?? LoanStatusType.All),
            principalMints: pagination?.principalMint ? [pagination.principalMint] : [],
            pageSize: pagination?.pageSize,
            page: pagination?.page,
            sortSide: pagination?.sortSide ?? SortDirection.Asc,
            sortType: pagination?.sortType ?? LoanInfoSorting.StartTime,
            collateralMints: loanFilterRaw.collateralMints ?? [],
            orderFundingTypes: loanFilterRaw.orderFundingTypes
        };
    }, [loanFilterRaw, pagination]);

    const skip = params?.skip;
    const {
        data: rawData,
        isLoading: queryLoading,
        isFetching: loanInfosFetching
    } = useLoanInfosQuery(loanFilter, { skip });

    const loans = rawData ? Object.values(rawData.loanInfos) : undefined;

    const { mints, whirlpoolMints, whirlpoolAddresses, raydiumMints, raydiumAddresses } =
        useLoanInfoResponseMints(rawData);

    const { data: whirlpools, isFetching: whirlpoolsFetching } = useWhirlpoolPositions(whirlpoolMints);
    const { data: raydiumPools, isFetching: raydiumPoolsFetching } = useRaydiumPositions(raydiumMints);

    const positionNftToPool = useMemoizedKeyMap(raydiumPools, (r) => r.position.nftMint);
    const positionToWhirlpool = useMemoizedKeyMap(whirlpools, (w) => w.position.positionMint);

    const { data: whirlpoolsOffchain } = useWhirlpoolsOffchainQuery(
        whirlpoolAddresses?.length ? { addresses: whirlpoolAddresses } : skipToken
    );

    const { data: raydiumOffchain } = useRaydiumOffchainQuery(
        raydiumAddresses?.length ? { ids: raydiumAddresses } : skipToken
    );

    const whirlpoolToOffchain = useMemoizedKeyMap(whirlpoolsOffchain?.data ?? [], (w) => w.address);
    const raydiumToOffchain = useMemoizedKeyMap(raydiumOffchain?.data ?? [], (r) => r.id);
    const { getUsdPrice, getOracle, isLoading: pricesLoading } = useOraclePrices(mints, PriceFetchType.Twap);

    const getClmmPosition = useCallback(
        (assetMint: string) => {
            const whirlpool = positionToWhirlpool?.get(assetMint);
            if (isWhirlpoolPosition(whirlpool)) return whirlpool;
            const raydium = positionNftToPool?.get(assetMint);
            if (isRaydiumPosition(raydium)) return raydium;
            return undefined;
        },
        [positionToWhirlpool, positionNftToPool]
    );

    const getUsdPriceWithClmmPosition = useCallback(
        (assetMint: string, assetType?: AssetTypeIdentifier) => {
            let usdPrice = getUsdPrice(assetMint, UncertaintyType.Subtract);
            if (assetType === AssetTypeIdentifier.OrcaPosition) {
                const whirlpool = positionToWhirlpool?.get(assetMint);
                if (whirlpool && whirlpool.totalPrice !== undefined) {
                    usdPrice = whirlpool.totalPrice;
                }
            } else if (assetType === AssetTypeIdentifier.RaydiumPosition) {
                const raydium = positionNftToPool?.get(assetMint);
                if (raydium && raydium.totalPrice !== undefined) {
                    usdPrice = raydium.totalPrice;
                }
            }
            return usdPrice;
        },
        [getUsdPrice, positionToWhirlpool, positionNftToPool]
    );

    const { createPastCollateralExpanded } = useGenerateUiExpanded(mints);
    const { tokensLoading, getMetadata, convertLoanEvents, convertLoanInfo } = useAbfTypesToUiConverter(mints);

    const isFetching = loanInfosFetching || whirlpoolsFetching || raydiumPoolsFetching;
    const isLoading = queryLoading || tokensLoading || pricesLoading || isFetching;

    const data: AbfLoanExpanded[] | undefined = useMemo(() => {
        if (isLoading || isFetching) return undefined;
        if (skip || !loans) return [];

        const validLoans = loans
            .map((rawLoanInfoResponse: LoanInfoResponse): NullableRecord<AbfLoanExpanded> | undefined => {
                // Used to determine wether or not we should use past or current ledgers
                const loanClosed = rawLoanInfoResponse.loan.closed;
                const defaultLedger = getRawZcLedger(rawLoanInfoResponse);

                const principalMint = defaultLedger?.principalMint;
                const principalMetadata = principalMint ? getMetadata(principalMint) : FALLBACK_METADATA;

                if (!principalMetadata) {
                    return undefined;
                }

                const loanInfoResponse: LoanInfoResponse = convertLoanInfo(rawLoanInfoResponse);

                const ledgerExpanded: LedgerExpanded[] = loanInfoResponse.ledgers.map((l) => ({
                    ...l,
                    ledgerDebt: getLoanTotalDebtForLedger(l),
                    isFilled: l.principalDue === l.principalRepaid
                }));

                const pastLedgerExpanded: LedgerExpanded[] = loanInfoResponse.pastLedgers.map((l) => ({
                    ...l,
                    ledgerDebt: getLoanTotalDebtForLedger(l),
                    isFilled: l.principalDue === l.principalRepaid
                }));

                const loanCollateralExpanded: LoanCollateralExpanded[] = loanInfoResponse.collateral.map((c) => {
                    const metadata = getMetadata(c.assetIdentifier);
                    const whirlpoolOffchain = whirlpoolToOffchain?.get(c.assetIdentifier);
                    const raydiumOffchain = raydiumToOffchain?.get(c.assetIdentifier);

                    const loanCollateralExpanded = {
                        ...c,
                        metadata: metadata ? { ...metadata, whirlpoolOffchain, raydiumOffchain } : FALLBACK_METADATA,
                        usdPrice: getUsdPriceWithClmmPosition(c.assetMint, metadata?.assetType),
                        uncertainty: getOracle(c.assetIdentifier)?.uncertainty,
                        clmmPosition: getClmmPosition(c.assetMint)
                    };

                    return loanCollateralExpanded;
                });

                // Due to the complexity of the loan events, I have left the ui converter function as is. Meaning the events from loanInfoResponse are overwritten.
                const parsedLoanEvents: ParsedLoanEventExpanded[] = convertLoanEvents(
                    rawLoanInfoResponse,
                    rawLoanInfoResponse.matrixUpdates
                );

                const loanInfo: LoanInfo = {
                    loan: rawLoanInfoResponse.loan,
                    loanType: rawLoanInfoResponse.loanType,
                    collateralYield: rawLoanInfoResponse.collateralYield,
                    ledger: ledgerExpanded,
                    pastLedgers: pastLedgerExpanded,
                    collateral: loanCollateralExpanded,
                    events: parsedLoanEvents //Overwrites rawLoanInfoResponse.events from old logic
                };

                const pastLoanCollateralItems = createPastCollateralExpanded(
                    parsedLoanEvents,
                    positionToWhirlpool,
                    getUsdPrice
                );

                const loanInfoExpanded: LoanInfoExpanded = {
                    ...loanInfo,
                    pastCollateral: pastLoanCollateralItems
                };

                const lenders = [...new Set(rawLoanInfoResponse.ledgers.map((entry) => entry.strategy))];
                const loanExpandedBase: LoanExpandedBase = {
                    ...loanInfoExpanded,
                    index: 0, //Overriden later
                    status: LoanStatus.OngoingLoan, //Overriden later
                    borrower: rawLoanInfoResponse.loan.borrower,
                    lenders: lenders,
                    borrowerWAvgApy: calculateBorrowerWeightedAvgApy(loanClosed ? pastLedgerExpanded : ledgerExpanded)
                };

                const abfLoanExpanded: AbfLoanExpanded = {
                    ...loanExpandedBase,
                    principalMetadata: principalMetadata,
                    principalUsdPrice: getUsdPrice(principalMint, UncertaintyType.Add)
                };
                return abfLoanExpanded;
            })
            .filter(filterTruthy)
            .filter(filterNullableRecord)
            .map((loanExpanded, index) => ({
                ...loanExpanded,
                index,
                status: getLoanStatusFromLoan(loanExpanded)
            }));

        return validLoans;
    }, [
        isLoading,
        isFetching,
        skip,
        loans,
        getMetadata,
        convertLoanInfo,
        convertLoanEvents,
        createPastCollateralExpanded,
        positionToWhirlpool,
        getUsdPrice,
        whirlpoolToOffchain,
        raydiumToOffchain,
        getUsdPriceWithClmmPosition,
        getOracle,
        getClmmPosition
    ]);

    return {
        data: isLoading ? undefined : data,
        totalLoans: rawData?.totalCount,
        isLoading,
        isFetching
    };
}

export function getZcLoanEndTime(loanInfo: LoanInfo | undefined) {
    if (!loanInfo) {
        return undefined;
    }
    const firstLedger = loanInfo.ledger[0];
    return firstLedger?.endTime;
}
export function getLoanSoldTime(loanExpanded: AbfLoanExpanded | undefined) {
    return loanExpanded?.events.find((e) => e.originalLender)?.eventTime;
}

export function getLoanRepaidAmount(loanExpanded: AbfLoanExpanded | undefined) {
    if (loanExpanded === undefined) {
        return 0;
    }

    return loanExpanded.events.reduce((prev, curr) => {
        if (curr.action === LoanEventActionType.RepayPrincipal) {
            return curr.amount + prev;
        } else {
            return prev;
        }
    }, 0);
}

export function isLoanActive(loanExpanded: AbfLoanExpanded | undefined) {
    if (!loanExpanded) return false;
    return [LoanStatus.OngoingLoan].includes(loanExpanded.status) || isLoanInRefinanceGrace(loanExpanded);
}
export function isLoanInRefinanceGrace(loanExpanded: AbfLoanExpanded | undefined) {
    if (!loanExpanded) return false;
    return [LoanStatus.RefinanceGrace].includes(loanExpanded.status);
}

export function calculateAmountRepaid(metaData: ParsedRepayPrincipalMetadata) {
    const principalDelta =
        metaData.postRepaymentPrincipalLedgerState.principalRepaid -
        metaData.preRepaymentPrincipalLedgerState.principalRepaid;

    const interestDelta =
        metaData.postRepaymentPrincipalLedgerState.interestRepaid -
        metaData.preRepaymentPrincipalLedgerState.interestRepaid;

    return principalDelta + interestDelta;
}

export function isLoanComplete(loanInfo: AbfLoanExpanded | undefined) {
    if (!loanInfo) return false;
    return loanInfo.loan.closed;
}

export function getTimeRestrictedMintsStatus(mints: string[] | undefined) {
    if (mints === undefined || mints.length === 0)
        return {
            status: false,
            mints: []
        };
    const affectedMints: string[] = [];
    const restrictedMints: string[] = [];
    const marketsOpen = getRestrictedMarketsStatus();
    for (const mint of mints) {
        if (mint in ASSET_TIME_RESTRICTIONS) {
            const restrictions = ASSET_TIME_RESTRICTIONS[mint];
            affectedMints.push(mint);
            if (restrictions.some((r) => !marketsOpen[r])) restrictedMints.push(mint);
        }
    }
    return {
        status: restrictedMints.length > 0,
        mints: restrictedMints.length > 0 ? restrictedMints : affectedMints
    };
}

export function getDerivedCollateralAssetMints(asset: Collateral | undefined) {
    if (!asset) return [];
    if (asset.clmmPosition) {
        return [asset.clmmPosition.tokenA.mint, asset.clmmPosition.tokenB.mint];
    }
    return [asset.metadata.mint];
}

export function getDerivedLoanAssetMints(loanInfo: AbfLoanExpanded | undefined) {
    return loanInfo
        ? [
              ...loanInfo.collateral
                  .map((c) => {
                      return c.clmmPosition
                          ? [c.clmmPosition.tokenA.mint, c.clmmPosition.tokenB.mint]
                          : [c.metadata.mint];
                  })
                  .flat(),
              loanInfo.principalMetadata.mint
          ]
        : [];
}

export function isLoanWaitingLiquidation(loanInfo: AbfLoanExpanded | undefined) {
    if (!loanInfo) return false;

    if (!isBeforeNow((getZcLoanEndTime(loanInfo) ?? 0) + LOAN_REFINANCE_GRACE_BEFORE_DEFAULT)) {
        const ledgersPastDue = loanInfo?.ledger.filter((data) => !isLedgerInRefinanceGrace(loanInfo, data));
        return ledgersPastDue?.length > 0;
    }

    return false;
}

export function isLoanDefaulted(loanInfo: AbfLoanExpanded | undefined) {
    if (!loanInfo) return false;
    return loanInfo.status === LoanStatus.DefaultedLoan;
}

export function isLoanLiquidated(loanInfo: AbfLoanExpanded | undefined) {
    if (!loanInfo) return false;

    if (loanInfo.events.some((e) => isLiquidateEvent(e.action))) {
        return true;
    }

    return false;
}

// can be called on a fully liquidated loan, on a loan thats in temporary default standing
export function getLoanDefaultedDate(loanInfo: AbfLoanExpanded | undefined) {
    if (!loanInfo || !isLoanDefaulted(loanInfo)) return undefined;

    if (loanInfo.loan.closed) return loanInfo.pastLedgers.reduce((prev, curr) => Math.max(prev, curr.endTime), 0);

    return loanInfo.ledger.reduce((prev, curr) => Math.max(prev, curr.endTime), 0);
}

function getFullLiquidationEvent(events: ParsedLoanEventExpanded[]): ParsedLoanEventExpanded | undefined {
    const liquidationEvents = events.filter((e) => isLiquidateEvent(e.action));
    const fullLiquidationEvent = liquidationEvents.find(
        (e) => e.actionMetadata[LoanEventActionType.Liquidate].liquidationType === LiquidationType.FullLiquidation
    );
    return fullLiquidationEvent;
}

function getMaxRepaidEventTimeFromEvents(loanInfo: AbfLoanExpanded) {
    const events = loanInfo.events;

    const fullLiquidationEvent = getFullLiquidationEvent(events);
    if (fullLiquidationEvent?.eventTime) {
        return fullLiquidationEvent.eventTime;
    }

    const repaidEvents = events.filter((e) => isRepayEvent(e.action));
    return Math.max(...repaidEvents.map((e) => e.eventTime));
}

export function getLoanCompletedDate(loanInfo: AbfLoanExpanded | undefined) {
    if (!loanInfo || !isLoanComplete(loanInfo)) return undefined;
    return getMaxRepaidEventTimeFromEvents(loanInfo);
}

export function getMostRecentPaymentDate(loanInfo: AbfLoanExpanded | undefined) {
    const lastLedger = loanInfo?.ledger.filter((l) => l.isFilled)?.pop();
    if (!lastLedger || !loanInfo) return undefined;
    return lastLedger.endTime + loanInfo.loan.startTime;
}

export function getLoanMissedPayments(loan: AbfLoanExpanded) {
    return loan.ledger.filter((l) => isPastNow(l.endTime + loan.loan.startTime) && !l.isFilled);
}

export function getLoanPaymentsInGrace(loan: AbfLoanExpanded) {
    return loan.ledger.filter((l) => {
        const dueTimeWithGrace = l.endTime + TIME.DAY * 2;
        const now = getUnixTs();
        return !l.isFilled && dueTimeWithGrace > now;
    });
}

export function isLedgerInRefinanceGrace(loan: AbfLoanExpanded | undefined, ledger: LedgerExpanded | undefined) {
    if (!loan || !ledger || (ledger && ledger.isFilled)) return false;
    const isRefinanceGrace = ledger.endTime + LOAN_REFINANCE_GRACE_BEFORE_DEFAULT > getUnixTs();
    const isRefinancePeriod = ledger.endTime < getUnixTs();

    if (isRefinanceGrace && isRefinancePeriod) return true;

    return false;
}

export function formatLoanDuration(loanExpanded: AbfLoanExpanded) {
    const [start, end] = [loanExpanded.loan.startTime, getZcLoanEndTime(loanExpanded)];
    const shortDuration =
        (end || 0) - start < TIME.DAY && convertAnyDate(start).getDate() === convertAnyDate(end).getDate();
    return shortDuration
        ? `${formatDate(end, "date")} ${formatDate(start, "time")} - ${formatDate(end, "time")}`
        : `${formatDate(start, "date")} - ${formatDate(end, "date")}`;
}

export enum LoanHealthStatus {
    Healthy = "Healthy",
    Warning = "Warning",
    Danger = "Danger"
}

export interface LoanHealthDetails {
    health: number;
    ltv: number;
    loanCollateralValue: number;
    totalOwedValue: number;
    liquidationThreshold: number | undefined;
    loanHealthStatus: LoanHealthStatus;
}

export function getHealthStatus(
    loanLtv: number | undefined,
    strategyLtv: number | undefined,
    strategyLqt: number | undefined
): LoanHealthStatus {
    if (loanLtv === undefined || strategyLtv === undefined || strategyLqt === undefined) {
        return LoanHealthStatus.Healthy;
    }

    const ltvThreshold = strategyLtv + 0.01; // LTV + 1%
    const liquidationThreshold = strategyLqt - 0.02; // LQT - 2%

    if (loanLtv > liquidationThreshold) {
        return LoanHealthStatus.Danger; // Red - beyond LQT-2%
    } else if (loanLtv >= ltvThreshold) {
        return LoanHealthStatus.Warning; // Yellow - between LTV+1% and LQT-2%
    } else {
        return LoanHealthStatus.Healthy; // Green - below LTV+1%
    }
}

// Uses first ledgerIndexToLtvInfo since only single asset collateral supported
export function calculateLoanHealth(loanExpanded: AbfLoanExpanded | undefined): LoanHealthDetails {
    if (!loanExpanded)
        return {
            health: 0,
            ltv: 0,
            loanCollateralValue: 0,
            totalOwedValue: 0,
            liquidationThreshold: 0,
            loanHealthStatus: LoanHealthStatus.Healthy
        };

    const loanCollateralValue = getLoanCollateralUsdValue(loanExpanded);
    const totalLedgersDebt = summarizeLedgerAccount(getZcLedger(loanExpanded)).totalOutstanding;
    const totalOwedValue = bsMath.tokenAMul(
        totalLedgersDebt,
        loanExpanded.principalUsdPrice ?? 0,
        loanExpanded.principalMetadata?.decimals ?? 0
    );

    if (totalOwedValue === 0) {
        return {
            health: Number.POSITIVE_INFINITY,
            ltv: 0,
            loanCollateralValue,
            totalOwedValue,
            liquidationThreshold: 0,
            loanHealthStatus: LoanHealthStatus.Healthy
        };
    }

    const liquidationThreshold = getZcLoanLiquidationThreshold(loanExpanded);
    const strategyLtv = getZcLoanLtv(loanExpanded);

    const ltv = loanCollateralValue > 0 ? totalOwedValue / loanCollateralValue : 0;

    const health = calculateHealthRatio(totalOwedValue, loanCollateralValue, liquidationThreshold);

    return {
        health,
        ltv,
        loanCollateralValue,
        totalOwedValue,
        liquidationThreshold,
        loanHealthStatus: getHealthStatus(ltv, strategyLtv, liquidationThreshold)
    };
}

export function getLoanCollateralUsdValue(loanExpanded: AbfLoanExpanded | undefined) {
    if (!loanExpanded) return 0;

    return loanExpanded.collateral
        .map((collateral) =>
            bsMath.tokenAMul(collateral.amount, collateral.usdPrice ?? 0, collateral.metadata?.decimals ?? 0)
        )
        .reduce((acc, value) => bsMath.add(acc, value) ?? 0, 0);
}

export function canLenderReclaimCollateral(loanExpanded: AbfLoanExpanded | undefined) {
    if (!loanExpanded) return false;

    if (!isLoanLiquidated(loanExpanded) && !isLoanDefaulted(loanExpanded)) return false;
    return true;
}

export function isLoanFullyLiquidated(loanExpanded: AbfLoanExpanded | undefined) {
    if (!loanExpanded) return false;
    const fullLiquidationEvent = getFullLiquidationEvent(loanExpanded.events);
    return !!fullLiquidationEvent;
}

export function getLoanStatusFromLoan(loanExpanded: AbfLoanExpanded): LoanStatus {
    const firstLedger = getZcLedger(loanExpanded);
    if (isLedgerInRefinanceGrace(loanExpanded, firstLedger)) return LoanStatus.RefinanceGrace;
    if (isLoanWaitingLiquidation(loanExpanded) || isLoanFullyLiquidated(loanExpanded)) return LoanStatus.DefaultedLoan;
    if (isLoanComplete(loanExpanded)) return LoanStatus.RepaidLoan;

    return LoanStatus.OngoingLoan;
} // Make sure this import exists or adjust as needed

export function getLoanOrcaCollateral(loanExpanded: AbfLoanExpanded | undefined) {
    const collateral = loanExpanded?.collateral.find((c) => !!c.clmmPosition && !!c.amount);
    if (collateral && collateral.clmmPosition && isWhirlpoolPosition(collateral.clmmPosition)) {
        return collateral.clmmPosition;
    }
    return undefined;
}

export function getLoanRaydiumCollateral(loanExpanded: AbfLoanExpanded | undefined) {
    const collateral = loanExpanded?.collateral.find((c) => !!c.clmmPosition && !!c.amount);
    if (collateral && collateral.clmmPosition && isRaydiumPosition(collateral.clmmPosition)) {
        return collateral.clmmPosition;
    }
    return undefined;
}

export function getLoanCollateralIdentifier(collateral: LoanCollateralExpanded | undefined): string | undefined {
    if (!collateral) return undefined;
    if (collateral.clmmPosition) {
        return getClmmPositionIdentifier(collateral.clmmPosition);
    }
    return collateral.assetIdentifier;
}
export function getLoanCollateralMint(collateral: LoanCollateralExpanded | undefined): string | undefined {
    if (!collateral) return undefined;
    if (collateral.clmmPosition) {
        return getClmmPositionMint(collateral.clmmPosition);
    }
    return collateral.metadata.mint;
}

export function getCollateralIdentifier(collateral: Collateral | undefined): string | undefined {
    if (!collateral) return undefined;
    if (collateral.clmmPosition) {
        return getClmmPositionIdentifier(collateral.clmmPosition);
    }
    return collateral.metadata.mint;
}

export function getCollateralMint(collateral: Collateral | undefined): string | undefined {
    if (!collateral) return undefined;
    if (collateral.clmmPosition) {
        return getClmmPositionMint(collateral.clmmPosition);
    }
    return collateral.metadata.mint;
}

export function isClmmCollateral(collateral: Collateral | undefined): boolean | undefined {
    if (!collateral) {
        return undefined;
    }
    const clmmPositionType = getClmmPositionType(collateral.clmmPosition ?? undefined);
    return (
        clmmPositionType === AssetTypeIdentifier.OrcaPosition ||
        clmmPositionType === AssetTypeIdentifier.RaydiumPosition
    );
}

export function getLoanStrategyIdentifier(loanExpanded: AbfLoanExpanded | undefined) {
    if (!loanExpanded) return undefined;
    return loanExpanded.loan.nonce;
}
export function getZcLedgerStrategyIdentifier(loanExpanded: AbfLoanExpanded | undefined) {
    if (!loanExpanded) return undefined;
    const firstLedger = getZcLedger(loanExpanded);
    if (!firstLedger) return undefined;
    return firstLedger.strategy;
}

export function getZcLoanCollateral(loanExpanded: AbfLoanExpanded | undefined): LoanCollateralExpanded | undefined {
    if (!loanExpanded?.collateral || loanExpanded.collateral.length === 0) {
        return undefined;
    }

    return loanExpanded.collateral[0];
}

export function getZcLoanLtv(loanExpanded: AbfLoanExpanded | undefined): number | undefined {
    if (!loanExpanded) return undefined;
    const firstLedger = getZcLedger(loanExpanded);
    if (!firstLedger) return undefined;

    return firstLedger.ltvRatios[0];
}
export function getZcLoanPastLtv(loanExpanded: AbfLoanExpanded | undefined): number | undefined {
    if (!loanExpanded) return undefined;
    const firstLedger = getZcPastLedger(loanExpanded);
    if (!firstLedger) return undefined;

    return firstLedger.ltvRatios[0];
}
export function getZcLoanLiquidationThreshold(loanExpanded: AbfLoanExpanded | undefined): number | undefined {
    if (!loanExpanded) return undefined;
    const firstLedger = getZcLedger(loanExpanded);
    if (!firstLedger) return undefined;

    return firstLedger.lqtRatios[0];
}

export function getZcLoanPastLiquidationThreshold(loanExpanded: AbfLoanExpanded | undefined): number | undefined {
    if (!loanExpanded) return undefined;
    const firstLedger = getZcPastLedger(loanExpanded);
    if (!firstLedger) return undefined;

    return firstLedger.lqtRatios[0];
}

export function getZcLedger(loanExpanded: AbfLoanExpanded | undefined): LedgerExpanded | undefined {
    if (!loanExpanded) return undefined;

    // AbfLoanExpanded case
    if (!loanExpanded.ledger || loanExpanded.ledger.length === 0) {
        return undefined;
    }
    return loanExpanded.ledger.find((l) => l.ledgerIndex === 0);
}

export function getZcPastLedger(loanExpanded: AbfLoanExpanded | undefined): LedgerExpanded | undefined {
    if (!loanExpanded) return undefined;
    if (!loanExpanded.pastLedgers || loanExpanded.pastLedgers.length === 0) {
        return undefined;
    }
    return loanExpanded.pastLedgers.find((l) => l.ledgerIndex === 0);
}

export function getPastOrPresentZcLedger(loanExpanded: AbfLoanExpanded | undefined) {
    return isLoanComplete(loanExpanded) ? getZcPastLedger(loanExpanded) : getZcLedger(loanExpanded);
}

// This function is used to process the loan info response. If there is no ledger info, we default to past ledgers.
export function getRawZcLedger(loanExpanded: LoanInfoResponse | undefined): Ledger | undefined {
    if (!loanExpanded) return undefined;

    return (
        loanExpanded.ledgers.find((l) => l.ledgerIndex === 0) ||
        loanExpanded.pastLedgers.find((l) => l.ledgerIndex === 0)
    );
}

export function getZcLedgerFromLoanInfo(loanInfo: LoanInfo | undefined): LedgerExpanded | undefined {
    if (!loanInfo?.ledger || loanInfo.ledger.length === 0) {
        return undefined;
    }

    return loanInfo.ledger[0];
}

export function getZcLenders(loanExpanded: AbfLoanExpanded | undefined): string[] | undefined {
    if (!loanExpanded) return undefined;
    if (loanExpanded.lenders) return loanExpanded?.lenders;

    return loanExpanded.ledger.map((l) => l.strategy);
}

export function getCollateralValuesAtIndex(
    loanExpanded: AbfLoanExpanded | undefined,
    collateralIndex: number
): CollateralLoanValues | undefined {
    if (!loanExpanded?.collateral || loanExpanded.collateral.length === 0) {
        return undefined;
    }

    const ltvs = [0, 0, 0, 0, 0] as LoanValueTuple;
    const lqts = [0, 0, 0, 0, 0] as LoanValueTuple;

    for (const ledger of loanExpanded.ledger) {
        const ltv = ledger.ltvRatios[collateralIndex];
        const lqt = ledger.lqtRatios[collateralIndex];
        ltvs[ledger.ledgerIndex] = ltv;
        lqts[ledger.ledgerIndex] = lqt;
    }

    return {
        ltvs,
        lqts
    };
}

export function getZcCollateral(loanExpanded: AbfLoanExpanded | undefined): LoanCollateralExpanded | undefined {
    if (!loanExpanded?.collateral || loanExpanded.collateral.length === 0) {
        return undefined;
    }

    return loanExpanded.collateral[0];
}

export function calculateZcLoanBorrowerApy(loanExpanded: AbfLoanExpanded | undefined): number | undefined {
    if (!loanExpanded?.ledger || loanExpanded.ledger.length === 0) {
        return undefined;
    }

    const firstLedger = getZcLedger(loanExpanded);
    if (!firstLedger) return undefined;

    return firstLedger.apy;
}

export function isLoanBorrower(loanExpanded: AbfLoanExpanded | undefined, user: string | undefined) {
    if (!loanExpanded || !user) return false;
    return loanExpanded.borrower === user;
}

// Only supports single asset collateral
export function getLoanName(loanExpanded: AbfLoanExpanded | undefined) {
    if (!loanExpanded) return "";
    const principalName = BsMetaUtil.getSymbol(loanExpanded?.principalMetadata);

    if (isLoanComplete(loanExpanded)) {
        if (!loanExpanded.pastCollateral.length) return principalName;
        const collateralName = BsMetaUtil.getSymbol(loanExpanded.pastCollateral[0].metadata);
        return `${principalName}/${collateralName}`;
    }
    const collateral = getZcCollateral(loanExpanded);
    if (!collateral) return principalName;
    if (
        isWhirlpoolMetadata(collateral.metadata) && // todo: when raydium live, add a util fn that encompasses all assetType cases
        collateral.metadata.whirlpoolOffchain
    ) {
        return `${principalName}/${getWhirlpoolName(collateral.metadata.whirlpoolOffchain)}`;
    }
    const collateralName = BsMetaUtil.getSymbol(collateral.metadata);
    return `${principalName}/${collateralName}`;
}

function getWhirlpoolName(whirlpool: WhirlpoolOffchain): string {
    return whirlpool.tokenA.symbol + "-" + whirlpool.tokenB.symbol;
}

export function useLoanFromParams(loanAddress: string | undefined): {
    loanExpanded: AbfLoanExpanded | undefined;
    isLoading: boolean;
    notFound: boolean;
} {
    const {
        data: loans,
        isLoading,
        isFetching
    } = useLoanInfos({
        loanFilter: {
            loanAddresses: loanAddress ? [loanAddress] : [],
            lenders: [],
            borrowers: [],
            principalMints: [],
            sortSide: SortDirection.Asc,
            sortType: LoanInfoSorting.StartTime,
            collateralMints: []
        },
        pagination: null,
        skip: !loanAddress
    });

    const loanExpanded = loans?.[0];
    return {
        loanExpanded,
        isLoading,
        notFound: !loanExpanded && !isLoading && !isFetching && !!loanAddress
    };
}

export function getUnclaimedAssets(loanExpanded: AbfLoanExpanded | undefined): LoanCollateralExpanded[] | undefined {
    // Only supports unclaimed assets for closed loans
    if (!loanExpanded?.loan.closed) {
        return loanExpanded?.collateral.filter((c) => c.amount);
    }
    return undefined;
}

// Will need to use principal prices for multi-principal loans
function calculateBorrowerWeightedAvgApy(ledgers: Ledger[]): number {
    if (!ledgers.length) return 0;

    const totalPrincipalDue = ledgers.reduce((sum, ledger) => sum + ledger.principalDue, 0);
    if (totalPrincipalDue === 0) return 0;

    return ledgers.reduce((sum, ledger) => sum + (ledger.apy * ledger.principalDue) / totalPrincipalDue, 0);
}

export function isManageLeverageEligable(loanExpanded: AbfLoanExpanded | undefined): boolean {
    const collateral = getZcCollateral(loanExpanded);
    if (!collateral) return false;

    const isSplMint = isCollateralSplMint(collateral);

    return isSplMint;
}

function useLoanInfoResponseMints(loanInfoResponse: AbfLoanResponse | undefined) {
    const mints = useMemo(() => getMintsFromLoan(loanInfoResponse), [loanInfoResponse]);
    const whirlpoolMints = useMemo(() => getWhirlpoolMintsFromLoan(loanInfoResponse), [loanInfoResponse]);
    const whirlpoolAddresses = useMemo(() => getWhirlpoolAddressesFromLoan(loanInfoResponse), [loanInfoResponse]);

    const raydiumMints = useMemo(() => getRaydiumMintsFromLoan(loanInfoResponse), [loanInfoResponse]);
    const raydiumAddresses = useMemo(() => getRaydiumAddressesFromLoan(loanInfoResponse), [loanInfoResponse]);

    const allMints = new Set<string>(combineCollections([mints, whirlpoolMints, raydiumMints]));

    return {
        mints,
        whirlpoolMints,
        whirlpoolAddresses,
        raydiumMints,
        raydiumAddresses,
        allMints: Array.from(allMints)
    };
}

function getMintsFromLoan(loanInfoResponse: AbfLoanResponse | undefined): string[] | undefined {
    // Returns all unique principal mints from all loans' ledgers
    if (!loanInfoResponse) return undefined;
    const mints = new Set<string>();
    for (const loan of loanInfoResponse.loanInfos) {
        for (const ledger of loan.ledgers) {
            mints.add(ledger.principalMint);
        }
    }
    return Array.from(mints);
}

function getWhirlpoolMintsFromLoan(loanInfoResponse: AbfLoanResponse | undefined): string[] | undefined {
    // Returns all unique asset mints for OrcaPosition collateral across all loans
    if (!loanInfoResponse) return undefined;
    const mints = new Set<string>();
    for (const loan of loanInfoResponse.loanInfos) {
        for (const collateral of loan.collateral) {
            if (collateral.assetType === AssetTypeIdentifier.OrcaPosition) {
                mints.add(collateral.assetMint);
            }
        }
    }
    return Array.from(mints);
}

function getWhirlpoolAddressesFromLoan(loanInfoResponse: AbfLoanResponse | undefined): string[] | undefined {
    // Returns all unique asset identifiers for OrcaPosition collateral across all loans
    if (!loanInfoResponse) return undefined;
    const addresses = new Set<string>();
    for (const loan of loanInfoResponse.loanInfos) {
        for (const collateral of loan.collateral) {
            if (collateral.assetType === AssetTypeIdentifier.OrcaPosition) {
                addresses.add(collateral.assetIdentifier);
            }
        }
    }
    return Array.from(addresses);
}

function getRaydiumMintsFromLoan(loanInfoResponse: AbfLoanResponse | undefined): string[] | undefined {
    // Returns all unique asset mints for RaydiumPosition collateral across all loans
    if (!loanInfoResponse) return undefined;
    const mints = new Set<string>();
    for (const loan of loanInfoResponse.loanInfos) {
        for (const collateral of loan.collateral) {
            if (collateral.assetType === AssetTypeIdentifier.RaydiumPosition) {
                mints.add(collateral.assetMint);
            }
        }
    }
    return Array.from(mints);
}

function getRaydiumAddressesFromLoan(loanInfoResponse: AbfLoanResponse | undefined): string[] | undefined {
    // Returns all unique asset identifiers for RaydiumPosition collateral across all loans
    if (!loanInfoResponse) return undefined;
    const addresses = new Set<string>();
    for (const loan of loanInfoResponse.loanInfos) {
        for (const collateral of loan.collateral) {
            if (collateral.assetType === AssetTypeIdentifier.RaydiumPosition) {
                addresses.add(collateral.assetIdentifier);
            }
        }
    }
    return Array.from(addresses);
}
